import { createPath, parsePath } from 'react-router-dom'

import {
  FROM_URL_REDIRECT_KEY,
  PAGE_ROUTES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'

import { matchPaths } from './stringUtils'
import { STICKY_QUERY_PARAMS } from '../constants/appConstants'

export { getRouteTitle } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/routeTitleUtils'

/**
 * Utility to preserve some query parameters during a route transition
 *
 * @param preserveParams List of parameters to copy from `current`, merge into `next` if it is not already set
 * @returns an updated search string
 */
export function preserveSearchParams(
  preserveParams: ReadonlyArray<string>,
  current: string | undefined,
  next: string | undefined,
): string | undefined {
  if (!current) {
    return next
  }

  const curParams = new URLSearchParams(current)
  const nextParams = new URLSearchParams(next)
  let modified = false
  for (const param of preserveParams) {
    if (curParams.has(param) && !nextParams.has(param)) {
      modified = true
      const values = curParams.getAll(param)
      values.forEach((val) => nextParams.append(param, val))
    }
  }

  // return the original search string if we did not modify anything
  return modified ? '?' + nextParams.toString() : next
}

/**
 * Builds a same-origin document-navigation target while carrying only the
 * app-wide query parameters that are safe to preserve across routes.
 */
export function addStickyQueryParams(pathname: string, currentSearch = window.location.search): string {
  return `${pathname}${preserveSearchParams(STICKY_QUERY_PARAMS, currentSearch, undefined) ?? ''}`
}

export const getLocalBaseUrl = (isCare = false, customer = '') => {
  return `https://${isCare ? 'care.' : customer ? customer + '.' : ''}localhost:4000`
}

/**
 * Sets a query parameter on an app-relative URL while keeping any `#fragment` in place, e.g.
 * `/faq#cancellation-policy` -> `/faq?fromUrlRedirect=true#cancellation-policy`.
 */
export const setUrlQueryParam = (url: string, key: string, value: string) => {
  const { pathname, search, hash } = parsePath(url)
  const params = new URLSearchParams(search)
  params.set(key, value)
  return createPath({ pathname, search: params.toString(), hash })
}

export const addFromUrlRedirectParam = (url: string) => setUrlQueryParam(url, FROM_URL_REDIRECT_KEY, 'true')

export const getIsProviderResultsPage = (pathname: string): boolean =>
  matchPaths(pathname, [
    `/secure/onboard/${PAGE_ROUTES.T1_MULTI_PROVIDERS}`,
    `/secure/onboard/${PAGE_ROUTES.T1_MULTI_PROVIDERS}/${PAGE_ROUTES.LATEST_PROVIDER_MATCHES}`,
    `/secure/onboard/${PAGE_ROUTES.COACHES}`,
    `/secure/onboard/${PAGE_ROUTES.COACHES}/${PAGE_ROUTES.LATEST_PROVIDER_MATCHES}`,
    `/secure/onboard/${PAGE_ROUTES.COACHING.COACHES}`,
  ])
